import { createTheme, Theme } from '@mui/material/styles'
import { PaletteMode, PaletteOptions } from '@mui/material'
import themeComponents from './themeComponents'

/**
 * Intent Intelligence design tokens.
 *
 * The consumer app's MUI theme, repainted to the "Intent Intelligence" design language established
 * on the redesigned About page (`src/pages/eater/about/About.tsx`). Three type roles (a serif
 * DISPLAY for headings, a sans BODY, a mono for labels/eyebrows), a warm neutral ground/surface,
 * and a single restrained SIGNAL accent (orange) that replaces the old brand blue/orange pair.
 *
 * NOTE (fonts): the licensed OFL webfonts are now self-hosted and bundled at app entry via
 * `src/fonts.ts` (imported from `src/index.tsx`) using @fontsource — no CDN / CSP exception, so they
 * render pixel-consistently across the Capacitor iOS/Android shells. The stacks below lead with the
 * bundled families and keep the original system fallbacks for the pre-font-load flash / edge cases.
 *   - DISPLAY serif   = Fraunces        (headings)
 *   - BODY sans       = Geist           (@fontsource/geist-sans declares the family "Geist Sans")
 *   - DATA/label mono = JetBrains Mono
 */

// ── Type roles ──────────────────────────────────────────────────────────────
export const SERIF_FONT =
  "'Fraunces', ui-serif, \"New York\", \"Iowan Old Style\", Georgia, \"Times New Roman\", serif"
export const SANS_FONT =
  "'Geist Sans', 'Geist', -apple-system, BlinkMacSystemFont, \"Segoe UI\", system-ui, \"Helvetica Neue\", Arial, sans-serif"
export const MONO_FONT =
  "'JetBrains Mono', ui-monospace, \"SF Mono\", \"Cascadia Code\", Menlo, monospace"

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1600,
  },
}

// ── Typography ──────────────────────────────────────────────────────────────
// Headings use the serif DISPLAY face at editorial weight (500) with tight tracking. Sizes use
// clamp() so they scale fluidly from ~360px phones up — mirroring About.tsx's scoped CSS — which
// also removes the need for per-heading max-width media queries. Body/button use the sans BODY
// face; `overline` and `caption` carry the mono-label ("eyebrow") treatment.
const typography = {
  fontFamily: SANS_FONT,
  fontSize: 14,
  h1: {
    fontFamily: SERIF_FONT,
    fontSize: 'clamp(2.25rem, 5vw, 3.35rem)',
    fontWeight: 500,
    lineHeight: 1.08,
    letterSpacing: '-0.015em',
  },
  h2: {
    fontFamily: SERIF_FONT,
    fontSize: 'clamp(1.75rem, 3.6vw, 2.5rem)',
    fontWeight: 500,
    lineHeight: 1.12,
    letterSpacing: '-0.015em',
  },
  h3: {
    fontFamily: SERIF_FONT,
    fontSize: 'clamp(1.4rem, 2.6vw, 1.9rem)',
    fontWeight: 500,
    lineHeight: 1.2,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontFamily: SERIF_FONT,
    fontSize: 'clamp(1.2rem, 2vw, 1.45rem)',
    fontWeight: 500,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
  },
  h5: {
    fontFamily: SERIF_FONT,
    fontSize: '1.2rem',
    fontWeight: 500,
    lineHeight: 1.3,
  },
  h6: {
    fontFamily: SANS_FONT,
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.4,
    letterSpacing: '-0.005em',
  },
  body1: {
    fontFamily: SANS_FONT,
    fontSize: '18px',
    lineHeight: '24px',
    fontWeight: 500,
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '16px',
      lineHeight: '22px',
    },
  },
  body2: {
    fontFamily: SANS_FONT,
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 500,
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '14px',
      lineHeight: '20px',
    },
  },
  subtitle1: {
    fontFamily: SANS_FONT,
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 500,
    [`@media (max-width:${breakpoints.values.sm}px)`]: {
      fontSize: '12px',
      lineHeight: '16px',
    },
  },
  subtitle2: {
    fontFamily: SANS_FONT,
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 500,
  },
  button: {
    fontFamily: SANS_FONT,
    fontWeight: 600,
    letterSpacing: 0,
    textTransform: 'none' as const,
  },
  // Mono-label / eyebrow treatment — uppercase, wide tracking, data-label feel.
  overline: {
    fontFamily: MONO_FONT,
    fontSize: '0.72rem',
    lineHeight: 1.5,
    fontWeight: 500,
    letterSpacing: '0.2em',
    textTransform: 'uppercase' as const,
  },
  caption: {
    fontFamily: SANS_FONT,
    fontSize: '12px',
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: '0.02em',
  },
}

// ── Semantic (non-brand) scales, shared across modes ────────────────────────
const success = {
  main: '#12B76A',
  light: '#32D583',
  dark: '#039855',
  contrastText: '#FFFFFF',
  50: '#ECFDF3',
  100: '#D1FADF',
  200: '#A6F4C5',
  300: '#6CE9A6',
  400: '#32D583',
  500: '#12B76A',
  600: '#039855',
  700: '#027A48',
  800: '#05603A',
  900: '#054F31',
}
const warning = {
  main: '#F79009',
  light: '#FDB022',
  dark: '#DC6803',
  contrastText: '#FFFFFF',
  50: '#FFFAEB',
  100: '#FEF0C7',
  200: '#FEDF89',
  300: '#FEC84B',
  400: '#FDB022',
  500: '#F79009',
  600: '#DC6803',
  700: '#B54708',
  800: '#93370D',
  900: '#7A2E0E',
  A100: '#FFFCF5',
}
const error = {
  main: '#F04438',
  light: '#F97066',
  dark: '#D92D20',
  contrastText: '#FFFFFF',
  50: '#FEF3F2',
  100: '#FEE4E2',
  200: '#FECDCA',
  300: '#FDA29B',
  400: '#F97066',
  500: '#F04438',
  600: '#D92D20',
  700: '#B42318',
  800: '#912018',
  900: '#7A271A',
}

// ── Brand tokens per mode ───────────────────────────────────────────────────
// SIGNAL is the single accent (orange, reframed from the old #FF7B54/#FF6B35). Used sparingly.
const light = {
  ground: '#F1F0EB',
  surface: '#FBFAF7',
  ink: '#191510',
  muted: '#6C665C',
  line: '#D9D5CC',
  signal: '#DA4E1C',
  signalInk: '#B23F14',
  // Signal (primary) scale.
  primary: {
    main: '#DA4E1C',
    light: '#E87145',
    dark: '#B23F14',
    contrastText: '#FFFFFF',
    50: '#FDF1EB',
    100: '#FADCCF',
    200: '#F5BDA5',
    300: '#EF9873',
    400: '#E87145',
    500: '#DA4E1C',
    600: '#C4451A',
    700: '#B23F14',
    800: '#8F3110',
    900: '#6E260C',
  },
  // Secondary reframed to warm ink/neutral so the orange stays the lone accent.
  secondary: {
    main: '#191510',
    light: '#3A342B',
    dark: '#0F0C09',
    contrastText: '#FBFAF7',
    50: '#F1F0EB',
    100: '#E4E1D9',
    300: '#9C9488',
    800: '#2C271F',
  },
  grey: {
    50: '#FBFAF7',
    100: '#F1F0EB',
    200: '#E7E4DD',
    300: '#D9D5CC',
    400: '#A69E90',
    500: '#6C665C',
    600: '#565049',
    700: '#3E3931',
    800: '#2C271F',
    900: '#191510',
    A100: '#FCFCFB',
    A200: '#14110D',
  },
  background: { default: '#F1F0EB', paper: '#FBFAF7' },
  text: { primary: '#191510', secondary: '#6C665C', disabled: '#A69E90' },
  divider: '#D9D5CC',
}

const dark = {
  ground: '#14110D',
  surface: '#1B1712',
  ink: '#EEE9E0',
  muted: '#9C9488',
  line: '#2C271F',
  signal: '#FF6C39',
  signalInk: '#FF8256',
  primary: {
    main: '#FF6C39',
    light: '#FF8B63',
    dark: '#E85420',
    contrastText: '#14110D',
    50: '#3A1C10',
    100: '#4E2413',
    200: '#6E3016',
    300: '#8F3D19',
    400: '#C4501E',
    500: '#FF6C39',
    600: '#FF8256',
    700: '#FF9E7B',
    800: '#FFBBA0',
    900: '#FFD7C6',
  },
  secondary: {
    main: '#EEE9E0',
    light: '#FBFAF7',
    dark: '#CFC8BC',
    contrastText: '#14110D',
    50: '#1B1712',
    100: '#221D16',
    300: '#6C665C',
    800: '#CFC8BC',
  },
  grey: {
    50: '#1B1712',
    100: '#221D16',
    200: '#2C271F',
    300: '#3A342B',
    400: '#6C665C',
    500: '#9C9488',
    600: '#B7AFA1',
    700: '#CFC8BC',
    800: '#E4DED3',
    900: '#EEE9E0',
    A100: '#14110D',
    A200: '#FBFAF7',
  },
  background: { default: '#14110D', paper: '#1B1712' },
  text: { primary: '#EEE9E0', secondary: '#9C9488', disabled: '#6C665C' },
  divider: '#2C271F',
}

const tokens = { light, dark }

const buildPalette = (mode: PaletteMode): PaletteOptions => {
  const t = tokens[mode]
  return {
    mode,
    primary: t.primary,
    secondary: t.secondary,
    grey: t.grey,
    background: t.background,
    common: { black: '#000000', white: '#FFFFFF' },
    success,
    warning,
    error,
    text: t.text,
    divider: t.divider,
  }
}

// Factory so both modes share typography, breakpoints and component overrides (the overrides read
// palette values through callbacks, so they adapt to whichever mode they're built with).
export const createAppTheme = (mode: PaletteMode): Theme =>
  createTheme({
    breakpoints,
    components: themeComponents,
    palette: buildPalette(mode),
    typography,
    shape: {
      borderRadius: 3,
    },
  })

// Default export stays the light theme so existing `import theme from './theme'` consumers are
// unchanged. `darkTheme` is provided for when a mode toggle is wired up.
const theme = createAppTheme('light')
export const darkTheme = createAppTheme('dark')

export default theme
